import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import { Plus, Search, Users, Lock, Globe, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog'
import { groupsApi, Group } from '@/lib/api'
import { formatNumber, getInitials } from '@/lib/utils'

export function GroupsPage() {
  const { groupId } = useParams()
  const [showCreateModal, setShowCreateModal] = useState(false)

  const { data: groupsData, isLoading } = useQuery({
    queryKey: ['groups'],
    queryFn: () => groupsApi.getGroups(),
  })

  const groups = groupsData?.data?.data || []

  if (groupId) {
    return <GroupView groupId={groupId} />
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-2xl font-bold">Groups</h1>
        <Button onClick={() => setShowCreateModal(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Create Group
        </Button>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input placeholder="Search groups..." className="pl-9" />
      </div>

      {isLoading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : groups.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Users className="mx-auto h-12 w-12 text-muted-foreground" />
            <h3 className="mt-4 font-display text-lg font-semibold">No groups yet</h3>
            <p className="mt-2 text-muted-foreground">
              Create or join a group to connect with others
            </p>
            <Button className="mt-4" onClick={() => setShowCreateModal(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Create Group
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {groups.map((group: Group, index: number) => (
            <motion.div
              key={group.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <GroupCard group={group} />
            </motion.div>
          ))}
        </div>
      )}

      {/* Suggested groups */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Suggested Groups</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {['Music Lovers', 'Photography Club', 'Gaming Squad'].map((name, i) => (
              <div key={name} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Avatar className="h-10 w-10">
                    <AvatarFallback>{name[0]}</AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium">{name}</p>
                    <p className="text-xs text-muted-foreground">
                      {(10 - i) * 123} members
                    </p>
                  </div>
                </div>
                <Button size="sm" variant="outline">
                  Join
                </Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <CreateGroupModal open={showCreateModal} onOpenChange={setShowCreateModal} />
    </div>
  )
}

function CreateGroupModal({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [privacy, setPrivacy] = useState<'public' | 'private'>('public')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const createMutation = useMutation({
    mutationFn: () => groupsApi.createGroup({ name, description, privacy }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groups'] })
      onOpenChange(false)
      setName('')
      setDescription('')
      setPrivacy('public')
    },
    onSettled: () => {
      setIsSubmitting(false)
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) return
    setIsSubmitting(true)
    createMutation.mutate()
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent onClose={() => onOpenChange(false)}>
        <DialogHeader>
          <DialogTitle className="font-display">Create a Group</DialogTitle>
          <DialogDescription>
            Create a new group to connect with others who share your interests.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="mt-4 space-y-4">
          <div className="space-y-2">
            <label htmlFor="name" className="text-sm font-medium">
              Group Name
            </label>
            <Input
              id="name"
              placeholder="Enter group name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <label htmlFor="description" className="text-sm font-medium">
              Description
            </label>
            <textarea
              id="description"
              placeholder="What's your group about?"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full min-h-[80px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-none"
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Privacy</label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="privacy"
                  value="public"
                  checked={privacy === 'public'}
                  onChange={() => setPrivacy('public')}
                  className="accent-primary"
                />
                <Globe className="h-4 w-4" />
                <span className="text-sm">Public</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="privacy"
                  value="private"
                  checked={privacy === 'private'}
                  onChange={() => setPrivacy('private')}
                  className="accent-primary"
                />
                <Lock className="h-4 w-4" />
                <span className="text-sm">Private</span>
              </label>
            </div>
          </div>
          <DialogFooter className="mt-6">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting || !name.trim()}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create Group
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function GroupCard({ group }: { group: Group }) {
  return (
    <a href={`/groups/${group.id}`}>
      <Card className="transition-colors hover:border-primary/50">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <Avatar className="h-14 w-14">
              <AvatarImage src={group.avatarUrl} />
              <AvatarFallback>{getInitials(group.name)}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="font-medium truncate">{group.name}</h3>
                {group.privacy === 'private' ? (
                  <Lock className="h-3 w-3 text-muted-foreground" />
                ) : (
                  <Globe className="h-3 w-3 text-muted-foreground" />
                )}
              </div>
              <p className="text-sm text-muted-foreground line-clamp-2">
                {group.description}
              </p>
              <p className="mt-2 text-xs text-muted-foreground">
                {formatNumber(group.membersCount)} members
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </a>
  )
}

function GroupView({ groupId }: { groupId: string }) {
  const { data: groupData, isLoading } = useQuery({
    queryKey: ['group', groupId],
    queryFn: () => groupsApi.getGroup(groupId),
  })

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  const group = groupData?.data

  if (!group) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <h3 className="font-display text-lg font-semibold">Group not found</h3>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      <Card className="overflow-hidden">
        <div className="h-32 bg-gradient-to-r from-primary/30 to-accent/30 sm:h-48">
          {group.coverUrl && (
            <img src={group.coverUrl} alt="" className="h-full w-full object-cover" />
          )}
        </div>
        <CardContent className="pt-6">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="font-display text-2xl font-bold">{group.name}</h1>
              <p className="text-muted-foreground">
                {formatNumber(group.membersCount)} members
              </p>
            </div>
            <Button>Join Group</Button>
          </div>
          <p className="mt-4">{group.description}</p>
        </CardContent>
      </Card>
    </div>
  )
}
